import { openDB } from 'idb';

const DB_NAME = 'my-app-db';
const STORE_NAME = 'images';
const PENDING_MESSAGES_STORE = 'pending_messages';
const PENDING_POST_ATTACHMENTS_STORE = 'pending_post_attachments';
const AUXBOX_AUDIO_STORE = 'auxbox_audio_blobs';

export async function getDb() {
  return openDB(DB_NAME, 4, { // Bump version to 4 for AuxBox audio store
    upgrade(db, oldVersion, newVersion, transaction) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('uploaded', 'uploaded', { unique: false });
      }
      if (!db.objectStoreNames.contains(PENDING_MESSAGES_STORE)) {
        db.createObjectStore(PENDING_MESSAGES_STORE, { keyPath: 'tempId' });
      }
      if (!db.objectStoreNames.contains(PENDING_POST_ATTACHMENTS_STORE)) {
        db.createObjectStore(PENDING_POST_ATTACHMENTS_STORE, { keyPath: 'localId' });
      }
      if (!db.objectStoreNames.contains(AUXBOX_AUDIO_STORE)) {
        db.createObjectStore(AUXBOX_AUDIO_STORE, { keyPath: 'id' });
      }
    },
  });
}

export async function savePendingMessage(message: any) {
  const db = await getDb();
  await db.put(PENDING_MESSAGES_STORE, message);
}

export async function getAllPendingMessages() {
  const db = await getDb();
  return db.getAll(PENDING_MESSAGES_STORE);
}

export async function deletePendingMessage(tempId: any) {
  const db = await getDb();
  await db.delete(PENDING_MESSAGES_STORE, tempId);
}

export async function saveImage(imageData: Blob | string | ArrayBuffer) {
  const db = await getDb();
  // store blob or string data directly
  const id = await db.add(STORE_NAME, { data: imageData, created: Date.now() });
  return id;
}

export async function getAllImages() {
  const db = await getDb();
  return db.getAll(STORE_NAME);
}

export async function deleteImage(id: number) {
  const db = await getDb();
  await db.delete(STORE_NAME, id);
}

export async function markImageUploaded(id: number, uploadedUrl: string) {
  const db = await getDb();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  const rec = await store.get(id as any);
  if (!rec) return;
  rec.uploaded = true;
  rec.uploadedUrl = uploadedUrl;
  await store.put(rec);
  await tx.done;
}

export async function getPendingUploads() {
  const db = await getDb();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  // use index to query records where uploaded is not true
  const all = await store.getAll();
  return all.filter((r: any) => !r.uploaded);
}

export async function savePendingPostAttachments(localId: number, attachments: { file: File, type: string, isLive?: boolean }[], coverImage: { file: File } | null) {
  const db = await getDb();
  await db.put(PENDING_POST_ATTACHMENTS_STORE, {
    localId,
    attachments: attachments.map(a => ({ file: a.file, type: a.type, isLive: a.isLive, name: a.file.name })),
    coverImage: (coverImage && coverImage.file) ? { file: coverImage.file, name: coverImage.file.name } : null
  });
}

export async function getPendingPostAttachments(localId: number) {
  const db = await getDb();
  return db.get(PENDING_POST_ATTACHMENTS_STORE, localId);
}

export async function deletePendingPostAttachments(localId: number) {
  const db = await getDb();
  await db.delete(PENDING_POST_ATTACHMENTS_STORE, localId);
}

export async function saveAuxBoxAudioBlob(trackId: string, blob: Blob | File) {
  try {
    const db = await getDb();
    await db.put(AUXBOX_AUDIO_STORE, { id: trackId, blob, updated: Date.now() });
  } catch (e) {
    console.error('Failed to save AuxBox audio blob to IndexedDB:', e);
  }
}

export async function getAuxBoxAudioBlob(trackId: string): Promise<Blob | File | null> {
  try {
    const db = await getDb();
    const res = await db.get(AUXBOX_AUDIO_STORE, trackId);
    return res ? res.blob : null;
  } catch (e) {
    console.error('Failed to get AuxBox audio blob from IndexedDB:', e);
    return null;
  }
}

export async function deleteAuxBoxAudioBlob(trackId: string) {
  try {
    const db = await getDb();
    await db.delete(AUXBOX_AUDIO_STORE, trackId);
  } catch (e) {
    console.error('Failed to delete AuxBox audio blob from IndexedDB:', e);
  }
}

export async function getAllAuxBoxAudioBlobs(): Promise<Record<string, Blob | File>> {
  try {
    const db = await getDb();
    const all = await db.getAll(AUXBOX_AUDIO_STORE);
    const map: Record<string, Blob | File> = {};
    if (all) {
      for (const item of all) {
        if (item && item.id && item.blob) {
          map[item.id] = item.blob;
        }
      }
    }
    return map;
  } catch (e) {
    console.error('Failed to get all AuxBox audio blobs from IndexedDB:', e);
    return {};
  }
}

